import { TFunction } from "utils";

import { PRACTICE_SLUG, PracticeCrop, PracticeFCPO } from "models";

type CropDetailsPracticeSlug = PracticeFCPO["type"];

export type CropDetailsPracticeCatalogs = Record<CropDetailsPracticeSlug, Array<{ id: string; name: string }> | undefined>;

export type CropDetailsLine = {
  key: CropDetailsPracticeSlug | "residue";
  label: string;
  entries: string[];
};

const getPracticeNames = (cropPractices: PracticeFCPO[], practiceSlug: CropDetailsPracticeSlug, practiceCatalogs: CropDetailsPracticeCatalogs): string[] => {
  const practiceNames = cropPractices
    .filter((practice) => practice.type === practiceSlug)
    .sort((first, second) => (first.startDate ?? "").localeCompare(second.startDate ?? ""))
    .map((practice) => practiceCatalogs[practiceSlug]?.find((catalogPractice) => catalogPractice.id === practice.assignedPracticeId)?.name)
    .filter((practiceName): practiceName is string => !!practiceName);

  return Array.from(new Set(practiceNames));
};

// mirrors the crop form: the residue question only exists for crops that produce visible residue
const getResidueEntry = (hasResidue: boolean | null | undefined, practiceCrop: PracticeCrop | undefined, t: TFunction): string | null => {
  if (!practiceCrop?.residue || practiceCrop.residueHidden || hasResidue === null || hasResidue === undefined) return null;
  return t(`fieldTimeline:cropDetailsPanel.residue.${practiceCrop.residueType ?? "residue"}.${hasResidue ? "left" : "removed"}`);
};

// only practices that have actually been entered produce a line, so an empty array means there is nothing to show
export const getCropDetailsLines = ({
  cropPractices,
  hasResidue,
  practiceCrop,
  practiceCatalogs,
  t,
}: {
  cropPractices: PracticeFCPO[];
  hasResidue: boolean | null | undefined;
  practiceCrop: PracticeCrop | undefined;
  practiceCatalogs: CropDetailsPracticeCatalogs;
  t: TFunction;
}): CropDetailsLine[] => {
  const residueEntry = getResidueEntry(hasResidue, practiceCrop, t);

  const lines: CropDetailsLine[] = [
    {
      key: PRACTICE_SLUG.TILLAGES,
      label: t("fieldTimeline:cropDetailsPanel.tillages"),
      entries: getPracticeNames(cropPractices, PRACTICE_SLUG.TILLAGES, practiceCatalogs),
    },
    { key: "residue", label: t("fieldTimeline:cropDetailsPanel.residueManagement"), entries: residueEntry ? [residueEntry] : [] },
    {
      key: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      label: t("fieldTimeline:cropDetailsPanel.organicFertilizers"),
      entries: getPracticeNames(cropPractices, PRACTICE_SLUG.ORGANIC_FERTILIZERS, practiceCatalogs),
    },
    {
      key: PRACTICE_SLUG.MINERAL_FERTILIZERS,
      label: t("fieldTimeline:cropDetailsPanel.mineralFertilizers"),
      entries: getPracticeNames(cropPractices, PRACTICE_SLUG.MINERAL_FERTILIZERS, practiceCatalogs),
    },
  ];

  return lines.filter((line) => line.entries.length > 0);
};
